import { useEffect } from 'react'
import { motion } from 'framer-motion'
import { MaskedName, PH_UNMASK } from '@/lib/privacyMask'

interface AnalyzingScreenProps {
  onComplete: () => void
  // Optional — when provided (May 2026 wizard), copy reads "Analyzing
  // {Name}'s results…". Without it (legacy QuestionFlow), copy falls back
  // to the generic line below.
  learnerName?: string
}

export function AnalyzingScreen({ onComplete, learnerName }: AnalyzingScreenProps) {
  useEffect(() => {
    const timer = setTimeout(onComplete, 1500)
    return () => clearTimeout(timer)
  }, [onComplete])

  return (
    <div className="flex flex-1 items-center justify-center p-6" {...PH_UNMASK}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col items-center gap-6"
      >
        {/* Animated spinner */}
        <div className="relative flex h-20 w-20 items-center justify-center">
          {/* Outer spinning ring */}
          <motion.div
            className="absolute inset-0 rounded-full border-[3px] border-muted-foreground/15 border-t-primary"
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1.2, ease: 'linear' }}
          />
          {/* Inner pulse */}
          <motion.div
            className="h-10 w-10 rounded-full bg-primary/10"
            animate={{ scale: [1, 1.15, 1] }}
            transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
          />
        </div>

        {/* Text */}
        <div className="flex flex-col items-center gap-2">
          <h2 className="text-lg font-semibold">
            {learnerName ? (
              <>Analyzing <MaskedName>{learnerName}</MaskedName>&rsquo;s results…</>
            ) : (
              'Analyzing responses…'
            )}
          </h2>
          <p className="text-sm text-muted-foreground">
            Figuring out the best starting point
          </p>
        </div>

        {/* Staggered dots animation */}
        <div className="flex items-center gap-1.5">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="h-1.5 w-1.5 rounded-full bg-primary"
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{
                repeat: Infinity,
                duration: 1,
                delay: i * 0.2,
                ease: 'easeInOut',
              }}
            />
          ))}
        </div>
      </motion.div>
    </div>
  )
}
